import React, { useMemo, useState } from 'react';

import { Flex, Modal, ScrollArea, Select } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { VoiceCard } from 'components/SmartAssistantSections/PhoneAgentBasicsSection/VoiceCard';
import { useTranslation } from 'react-i18next';
import { AllOptions, VOICE_AGE_OPTIONS, VOICE_GENDER_OPTIONS } from 'shared/constants/phone-smart-assistant';
import { PhoneAgentBasicsFormValues } from 'shared/interfaces/SmartAssistant';
import { VoiceAge, VoiceConfiguration, VoiceGender } from 'shared/interfaces/Voice';

interface VoiceSelectorModalProps {
  opened: boolean;
  onClose: () => void;
  phoneAgentBasicsForm: UseFormReturnType<PhoneAgentBasicsFormValues>;
  audioRef: React.MutableRefObject<HTMLAudioElement | null>;
  availableVoices: VoiceConfiguration[];
  setPlayingVoiceId: (voiceId: string | null) => void;
  playingVoiceId: string | null;
}

export function VoiceSelectorModal({
  opened,
  onClose,
  phoneAgentBasicsForm,
  audioRef,
  availableVoices,
  setPlayingVoiceId,
  playingVoiceId
}: VoiceSelectorModalProps) {
  const { t } = useTranslation();
  const [selectedGender, setSelectedGender] = useState<AllOptions | VoiceGender>('all');
  const [selectedAge, setSelectedAge] = useState<AllOptions | VoiceAge>('all');

  // When updating the voice, we want to make sure the transcription language is in sync with the voice language
  const handleSelectVoice = (voiceConfiguration: VoiceConfiguration) => {
    phoneAgentBasicsForm.setFieldValue('voiceConfiguration', voiceConfiguration);
    onClose();
  };

  const filteredVoices = useMemo(() => {
    return availableVoices.filter((voice) => {
      const genderMatch = selectedGender === 'all' || voice.gender === selectedGender;
      const ageMatch = selectedAge === 'all' || voice.age === selectedAge;
      return genderMatch && ageMatch;
    });
  }, [selectedGender, selectedAge, availableVoices]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      padding="xl"
      title={t('SMART_ASSISTANT.PHONE_AGENT_BASICS_SECTION.VOICE.SELECT_VOICE_MODAL.TITLE')}>
      <Flex direction="row" gap="md" mb="md">
        <Select
          label={t('SMART_ASSISTANT.PHONE_AGENT_BASICS_SECTION.VOICE.SELECT_VOICE_MODAL.GENDER')}
          size="xs"
          data={VOICE_GENDER_OPTIONS}
          value={selectedGender}
          onChange={(value) => setSelectedGender(value as AllOptions | VoiceGender)}
          style={{ flex: 1 }}
        />
        <Select
          label={t('SMART_ASSISTANT.PHONE_AGENT_BASICS_SECTION.VOICE.SELECT_VOICE_MODAL.AGE')}
          size="xs"
          data={VOICE_AGE_OPTIONS}
          value={selectedAge}
          onChange={(value) => setSelectedAge(value as AllOptions | VoiceAge)}
          style={{ flex: 1 }}
        />
      </Flex>
      <ScrollArea scrollbarSize={0} h="500px">
        <Flex direction="column" gap="md">
          {filteredVoices.map((voiceConfig) => (
            <VoiceCard
              isPlaying={playingVoiceId === voiceConfig.voiceId}
              audioRef={audioRef}
              hideSettings
              key={voiceConfig.voiceId}
              voiceConfiguration={voiceConfig}
              onSelect={() => handleSelectVoice(voiceConfig)}
              setPlayingVoiceId={setPlayingVoiceId}
            />
          ))}
        </Flex>
      </ScrollArea>
    </Modal>
  );
}
